import type { ProductModelV2 } from '@develop/ia-api-library'
import { centsToEuro, formatCurrency } from '@develop/ia-utils'

export const useProductPrice = (_product?: MaybeRefOrGetter<ProductModelV2 | undefined>) => {
  const formatCentsToEuro = (cents: number) => formatCurrency(centsToEuro(cents))

  const product = computed(() => toValue(_product))

  // Raw prices
  const price = computed(() => product.value?.mainPrice?.value)
  const oldPrice = computed(() => product.value?.strikedPrice?.value)

  // Formatted prices
  const priceFormatted = computed(() => price.value ? formatCentsToEuro(price.value) : null)
  const oldPriceFormatted = computed(() => oldPrice.value ? formatCentsToEuro(oldPrice.value) : null)
  const basePriceFormatted = computed(() => product.value?.priceByUnit ? `${formatCentsToEuro(product.value?.priceByUnit.value)} / 1 ${product.value?.priceByUnit.unit}` : null)

  const hasBasePrice = computed(() => product.value?.priceByUnit != null)
  const hasDiscount = computed(() => product.value?.showStrikePrice && product.value?.strikedPrice != null)

  const discountPercent = computed(() => (product.value?.showPercentageBubble && product.value?.percentageBubble?.value) || null)
  const showDiscountTag = computed(() => discountPercent.value && discountPercent.value > 5)

  const joinNotes = (notes?: number[]) => notes ? notes.join(', ') : null

  // Price notes
  const priceNote = computed(() => joinNotes(product.value?.mainPrice?.notes))
  const oldPriceNote = computed(() => joinNotes(product.value?.strikedPrice?.notes))
  const discountNote = computed(() => joinNotes(product.value?.percentageBubble?.notes))

  // Total price
  const getTotalPrice = (amount: number) => product.value?.mainPrice ? product.value?.mainPrice.value * amount : 0
  const getTotalPriceFormatted = (amount: number) => formatCentsToEuro(getTotalPrice(amount))

  return {
    // raw
    price,
    oldPrice,
    // formatted
    priceFormatted,
    oldPriceFormatted,
    basePriceFormatted,
    // note
    priceNote,
    oldPriceNote,
    discountNote,
    // discount
    discountPercent,
    hasDiscount,
    hasBasePrice,
    showDiscountTag,
    getTotalPrice,
    getTotalPriceFormatted,
  }
}
